import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Upload, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getUserGymId } from "@/utils/gymAuth";
import { toast } from "sonner";
import { SubscriptionType } from "@/types/subscriptionType";
import { getSubscriptionTypes, deleteSubscriptionType } from "@/services/edgeFunctionWrappers/subscriptionTypesWrapper";
import { SubscriptionTypesTable } from "@/components/subscription-types/SubscriptionTypesTable";
import { SubscriptionTypeFormDialog } from "@/components/subscription-types/SubscriptionTypeFormDialog";
import { ImportSubscriptionTypesDialog } from "@/components/subscription-types/ImportSubscriptionTypesDialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useDraftResumeToast } from "@/hooks/useDraftResumeToast";

export default function SubscriptionTypes() {
  const { t } = useTranslation(["subscriptions", "adminui3"]);
  const gymId = getUserGymId();

  const [types, setTypes] = useState<SubscriptionType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<SubscriptionType | null>(null);

  // Création de pack interrompue par un reload → proposer de la reprendre.
  useDraftResumeToast([
    {
      formId: "subscription-type:new",
      message: t("subscriptionTypesPage.draftUnsaved", { ns: "adminui3" }),
      description: (d) => (d.name as string) || undefined,
      isEmpty: (d) => !d.name,
      onResume: () => {
        setEditingType(null);
        setFormDialogOpen(true);
      },
    },
  ]);

  const fetchTypes = useCallback(async () => {
    if (!gymId) return;
    
    setIsLoading(true);
    try {
      const data = await getSubscriptionTypes(gymId, showInactive);
      setTypes(data);
    } catch (error) {
      console.error('Error fetching subscription types:', error);
      toast.error(t("types.fetchError"));
    } finally {
      setIsLoading(false);
    }
  }, [gymId, showInactive, t]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const handleCreate = () => {
    setEditingType(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (type: SubscriptionType) => {
    setEditingType(type);
    setFormDialogOpen(true);
  };

  const handleDelete = async (type: SubscriptionType) => {
    if (!gymId) return;
    
    try {
      await deleteSubscriptionType(gymId, type.id);
      toast.success(t("types.deleteSuccess"));
      fetchTypes();
    } catch (error: any) {
      toast.error(error.message || t("types.deleteError"));
    }
  };

  const handleFormSuccess = () => {
    setFormDialogOpen(false);
    setEditingType(null);
    fetchTypes();
  };

  const handleImportSuccess = () => {
    setImportDialogOpen(false);
    fetchTypes();
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Package className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">{t("types.title")}</h1>
            <p className="text-muted-foreground">{t("types.subtitle")}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            {t("types.import")}
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            {t("types.create")}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t("types.list")}</CardTitle>
            <div className="flex items-center space-x-2">
              <Switch
                id="show-inactive"
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
              <Label htmlFor="show-inactive" className="text-sm text-muted-foreground">
                {t("types.showInactive")}
              </Label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <SubscriptionTypesTable
            types={types}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <SubscriptionTypeFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        editingType={editingType}
        onSuccess={handleFormSuccess}
      />

      <ImportSubscriptionTypesDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
}
